type Group = 'sfx' | 'ui' | 'music';

const SFX: Record<string, string> = {
  click: 'assets/audio/sfx/click.wav',
  credit: 'assets/audio/sfx/credit.mp3',
  denied: 'assets/audio/sfx/denied.wav',
  dayComplete: 'assets/audio/sfx/day-complete.mp3',
  fired: 'assets/audio/sfx/fired.mp3',
  alert: 'assets/audio/sfx/alert.mp3',
  tally: 'assets/audio/sfx/tally.mp3',
  stamp: 'assets/audio/sfx/stamp.wav',
  paperSlide1: 'assets/audio/sfx/paper-slide-1.mp3',
  paperSlide3: 'assets/audio/sfx/paper-slide-3.mp3',
  paperSlide4: 'assets/audio/sfx/paper-slide-4.mp3',
  memo: 'assets/audio/sfx/memo.mp3',
  audit: 'assets/audio/sfx/audit.mp3',
  crisis: 'assets/audio/sfx/crisis.mp3',
};

const MUSIC: Record<string, string> = {
  title: 'assets/audio/music/title.mp3',
  day1: 'assets/audio/music/day1.mp3',
  day2: 'assets/audio/music/day2.mp3',
  day3: 'assets/audio/music/day3.mp3',
  endingFired: 'assets/audio/music/ending-fired.mp3',
  endingWhistle: 'assets/audio/music/ending-whistle.mp3',
  credits: 'assets/audio/music/credits.mp3',
};

export class AudioManager {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private gains = new Map<Group, GainNode>();
  private musicSource: AudioBufferSourceNode | null = null;
  private currentMusic = '';
  private lastPlay = new Map<string, number>();
  muted = false;

  async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor = window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      const master = this.ctx.createGain();
      master.connect(this.ctx.destination);
      for (const g of ['sfx', 'ui', 'music'] as Group[]) {
        const gain = this.ctx.createGain();
        gain.connect(master);
        this.gains.set(g, gain);
      }
      this.gains.get('music')!.gain.value = 0.32;
      this.gains.get('sfx')!.gain.value = 0.8;
      this.gains.get('ui')!.gain.value = 0.5;
      void this.preload();
    }
    if (this.ctx.state !== 'running') await this.ctx.resume();
  }

  private async preload(): Promise<void> {
    const load = async (id: string, url: string) => {
      try {
        const data = await fetch(url).then((r) => r.arrayBuffer());
        this.buffers.set(id, await this.ctx!.decodeAudioData(data));
        if (id === this.currentMusic) this.startMusicSource(id);
      } catch (err) {
        console.warn(`[audio] failed to load ${id}`, err);
      }
    };
    await Promise.all([
      ...Object.entries(SFX).map(([id, url]) => load(`sfx:${id}`, url)),
      ...Object.entries(MUSIC).map(([id, url]) => load(`music:${id}`, url)),
    ]);
  }

  play(id: keyof typeof SFX, group: Group = 'sfx', volume = 1): void {
    if (!this.ctx || this.muted) return;
    const key = `sfx:${id}`;
    const buffer = this.buffers.get(key);
    if (!buffer) return;
    const now = performance.now();
    if (now - (this.lastPlay.get(key) ?? 0) < 60) return;
    this.lastPlay.set(key, now);
    const source = this.ctx.createBufferSource();
    const gain = this.ctx.createGain();
    gain.gain.value = volume;
    source.buffer = buffer;
    source.connect(gain).connect(this.gains.get(group)!);
    source.start();
  }

  music(id: keyof typeof MUSIC | ''): void {
    const key = id ? `music:${id}` : '';
    if (key === this.currentMusic) return;
    this.currentMusic = key;
    this.stopMusicSource();
    if (!key || !this.ctx) return;
    if (this.buffers.has(key)) this.startMusicSource(key);
    // else: preload() starts it when the buffer arrives
  }

  private startMusicSource(key: string): void {
    if (!this.ctx || key !== this.currentMusic) return;
    this.stopMusicSource();
    const buffer = this.buffers.get(key);
    if (!buffer) return;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(this.gains.get('music')!);
    source.start();
    this.musicSource = source;
  }

  private stopMusicSource(): void {
    if (this.musicSource) {
      try { this.musicSource.stop(); } catch { /* already stopped */ }
      this.musicSource.disconnect();
      this.musicSource = null;
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (!this.ctx) return;
    for (const gain of this.gains.values()) {
      gain.gain.value = muted ? 0 : gain === this.gains.get('music') ? 0.32 : gain === this.gains.get('ui') ? 0.5 : 0.8;
    }
  }
}
